// SPDX-License-Identifier: AGPL-3.0-or-later

import {AssistantConnectorSettings} from '@app/features/konvo_connections/components/AssistantConnectorCards';
import {KonvoAssistantDashboardsSection} from '@app/features/konvo_dashboards/components/KonvoAssistantDashboardsSection';
import {KonvoAssistantReportsSection} from './KonvoAssistantReportsSection';
// KONVO: user settings tab for the Claude-powered Assistant — connection
// card, on/off, model, what it may bring up on its own, conversation reset and
// the link to "What Konvo knows" (its notes and what it learned).

import {SettingsChoiceCards} from '@app/features/app/components/dialogs/shared/SettingsChoiceCards';
import {SettingsLinkRow, SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {AssistantVoiceSendDelay} from '@app/features/konvo_assistant/components/AssistantVoiceSendDelay';
import {AssistantVoiceSettings} from '@app/features/konvo_assistant/components/AssistantVoiceSettings';
import {VoiceModePlacementSetting} from '@app/features/konvo_assistant/components/VoiceModePlacementSetting';
import {getRecognitionConstructor} from '@app/features/konvo_assistant/state/AssistantDictation';
import AssistantSpeech from '@app/features/konvo_assistant/state/AssistantSpeech';
import {openKonvoAssistant} from '@app/features/konvo_connections/commands/KonvoAssistantCommands';
import {KonvoClaudeCard, KonvoCodexCard} from '@app/features/konvo_connections/components/KonvoConnections';
import KonvoConnections, {
	deviceTimezone,
	type KonvoAssistantAutonomy,
	type KonvoClaudeModel,
} from '@app/features/konvo_connections/state/KonvoConnections';
import {CornerSuggestionsSettings} from '@app/features/konvo_corner/components/CornerSuggestionsSettings';
import {openKnowsTab} from '@app/features/konvo_knows/components/KnowsActions';
import {M as KNOWS_M, KNOWS_TAB_DESCRIPTOR} from '@app/features/konvo_knows/components/KnowsMessages';
import KonvoKnows from '@app/features/konvo_knows/state/KonvoKnows';
import {ScheduledSettingsRow} from '@app/features/konvo_scheduled/components/ScheduledSettingsLink';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {
	ArrowCounterClockwiseIcon,
	BrainIcon,
	CaretRightIcon,
	ChatCircleDotsIcon,
	ChatCircleIcon,
	GlobeIcon,
	MicrophoneIcon,
	SparkleIcon,
	SpeakerHighIcon,
} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {KonvoAssistantProactiveSection} from '@app/features/user/components/modals/tabs/KonvoAssistantProactiveSection';
import assistantStyles from '@app/features/user/components/modals/tabs/KonvoAssistantSettingsTab.module.css';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo, useState} from 'react';

const ENABLED_DESCRIPTOR = msg({
	message: 'Answer my messages in the Assistant DM',
	comment: 'Assistant settings toggle.',
});
const ENABLED_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Switch off to pause the assistant without disconnecting your account. Reminders and notifications keep working.',
	comment: 'Description under the assistant enable toggle.',
});
const RESET_DESCRIPTOR = msg({message: 'Start a new conversation', comment: 'Assistant reset button.'});
const RESET_DONE_DESCRIPTOR = msg({
	message: 'Fresh start. Your next message begins a new conversation.',
	comment: 'Toast after resetting the assistant conversation.',
});
const OPEN_DESCRIPTOR = msg({message: 'Open Assistant DM', comment: 'Button in assistant settings.'});
const FAILED_DESCRIPTOR = msg({
	message: 'That did not save. Try again.',
	comment: 'Toast when an assistant setting fails.',
});

/** Which timezone the assistant reads "tomorrow at 10" in, with a one-tap fix when this device differs. */
function AssistantTimezoneRow({
	timezone,
	disabled,
	onUseDevice,
}: {
	timezone: string;
	disabled: boolean;
	onUseDevice: (timezone: string) => void;
}) {
	const device = deviceTimezone();
	return (
		<SettingsRow
			icon={<GlobeIcon size={20} />}
			title={
				<Trans>
					Times are read and written in <strong>{timezone}</strong>.
				</Trans>
			}
			control={() =>
				device && device !== timezone ? (
					<Button
						variant="secondary"
						small
						disabled={disabled}
						onClick={() => onUseDevice(device)}
						data-flx="user.konvo-assistant-tab.timezone-use-device"
					>
						<Trans>Use {device}</Trans>
					</Button>
				) : null
			}
			data-flx="user.konvo-assistant-tab.timezone"
		/>
	);
}

export const KonvoAssistantSettingsTab = observer(() => {
	const {i18n} = useLingui();
	const provider = KonvoConnections.assistantProvider;
	const status = provider === 'codex' ? KonvoConnections.codex : KonvoConnections.claude;
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		void KonvoConnections.refreshAssistant().catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)));
		void KonvoKnows.load();
	}, []);

	const autonomyOptions = useMemo(
		(): ReadonlyArray<RadioOption<KonvoAssistantAutonomy>> => [
			{
				value: 'suggest',
				name: i18n._(msg`Suggest only`),
				desc: i18n._(msg`Point out useful next steps, but do not prepare changes unless I ask.`),
			},
			{
				value: 'prepare',
				name: i18n._(msg`Prepare actions`),
				desc: i18n._(msg`Prepare editable drafts and action cards. Nothing executes until I approve it.`),
			},
			{
				value: 'approve',
				name: i18n._(msg`Act after approval`),
				desc: i18n._(msg`Recommend and prepare actions, then execute only after my explicit command or tap.`),
			},
			{
				value: 'routine',
				name: i18n._(msg`Run authorized routines`),
				desc: i18n._(
					msg`Only routines I separately authorize may run automatically. Everything else still waits for approval.`,
				),
			},
		],
		[i18n],
	);

	// The notes moved to What Konvo knows; the old `memory` field is no longer sent from here.
	const patch = async (body: {enabled?: boolean; model?: KonvoClaudeModel}) => {
		setSaving(true);
		try {
			if (provider === 'codex') await KonvoConnections.updateCodexSettings(body);
			else await KonvoConnections.updateClaudeSettings(body);
			return true;
		} catch {
			ToastCommands.error(i18n._(FAILED_DESCRIPTOR));
			return false;
		} finally {
			setSaving(false);
		}
	};

	const connected = status?.connected === true;
	const lastUsed = status?.last_used_at ? new Date(status.last_used_at) : null;

	return (
		<SettingsTabContainer data-flx="user.konvo-assistant-tab.container">
			<SettingsTabContent data-flx="user.konvo-assistant-tab.content">
				<SettingsSection
					id="konvo-assistant-connection"
					title={<Trans>Your assistant</Trans>}
					description={
						<Trans>
							Connect Claude Code or Codex, then choose which one answers in your Konvo Assistant DM. Each uses your own
							account.
						</Trans>
					}
					data-flx="user.konvo-assistant-tab.section.connection"
				>
					<div className={assistantStyles.cards} data-flx="user.konvo-assistant-tab.cards">
						<KonvoClaudeCard solo={MobileLayout.enabled} data-flx="user.konvo-assistant-tab.card" />
						<KonvoCodexCard solo={MobileLayout.enabled} />
					</div>
					<Segmented<'claude' | 'codex'>
						value={provider}
						options={(['claude', 'codex'] as const).map((choice) => ({
							value: choice,
							children: choice === 'claude' ? 'Claude Code' : 'Codex',
							disabled:
								saving ||
								!(choice === 'codex' ? KonvoConnections.codex?.connected : KonvoConnections.claude?.connected),
							'data-flx': `user.konvo-assistant-tab.provider.${choice}`,
						}))}
						onChange={(choice) => {
							setSaving(true);
							void KonvoConnections.selectAssistant(choice)
								.catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)))
								.finally(() => setSaving(false));
						}}
						aria-label={i18n._(msg`Active assistant`)}
						className={assistantStyles.provider}
						data-flx="user.konvo-assistant-tab.provider"
					/>
					{connected && (
						<p className={assistantStyles.status} data-flx="user.konvo-assistant-tab.status">
							{status?.turns ? (
								<Trans>
									{status.turns} replies so far
									{lastUsed ? ` · last used ${lastUsed.toLocaleString()}` : ''}
								</Trans>
							) : (
								<Trans>No conversations yet.</Trans>
							)}
							{status?.status === 'auth_error' && (
								<span style={{color: 'var(--status-danger, #f23f43)'}}>
									{' '}
									<Trans>Claude rejected your token — reconnect with a fresh one.</Trans>
								</span>
							)}
						</p>
					)}
				</SettingsSection>

				<CornerSuggestionsSettings />
				<AssistantConnectorSettings />
				<ScheduledSettingsRow />
				<KonvoAssistantReportsSection />
				<KonvoAssistantDashboardsSection />

				<SettingsSection id="konvo-assistant-speech" title={<Trans>Read aloud</Trans>}>
					<Switch
						label={i18n._(msg`Read replies aloud`)}
						description={i18n._(
							msg`Automatically read new replies while you are in the assistant conversation. You can still read any reply individually. Saved on this device.`,
						)}
						value={AssistantSpeech.automatic}
						onChange={AssistantSpeech.setAutomatic}
						icon={<SpeakerHighIcon size={20} />}
						data-flx="user.konvo-assistant-tab.auto-read"
					/>
					<Switch
						label={i18n._(msg`Microphone input`)}
						description={i18n._(
							msg`Show a dictation button in Home and assistant chat. Your browser may send audio to its speech service. Nothing is sent to the assistant until you review and send your message.`,
						)}
						value={AssistantSpeech.dictationEnabled}
						onChange={AssistantSpeech.setDictationEnabled}
						icon={<MicrophoneIcon size={20} />}
						data-flx="user.konvo-assistant-tab.dictation"
					/>
					{!getRecognitionConstructor() && (
						<p className={assistantStyles.note}>
							<Trans>
								Dictation is not available in this browser. You can keep this option enabled for a supported browser.
							</Trans>
						</p>
					)}
				</SettingsSection>

				<SettingsSection
					id="konvo-assistant-voice-mode"
					title={<Trans>Voice mode</Trans>}
					description={<Trans>Where Konvo's globe sits while you talk to it.</Trans>}
					data-flx="user.konvo-assistant-tab.section.voice-mode"
				>
					<VoiceModePlacementSetting />
				</SettingsSection>

				<SettingsSection
					id="konvo-assistant-voices"
					title={<Trans>Voices</Trans>}
					description={<Trans>How Konvo sounds when it reads replies and when you talk to it in voice mode.</Trans>}
					data-flx="user.konvo-assistant-tab.section.voices"
				>
					<AssistantVoiceSettings />
					<AssistantVoiceSendDelay />
				</SettingsSection>

				{connected && (
					<>
						<SettingsSection
							id="konvo-assistant-proactivity"
							title={<Trans>Proactivity</Trans>}
							description={
								<Trans>Choose how far Konvo may go when it notices something useful without being asked.</Trans>
							}
							data-flx="user.konvo-assistant-tab.section.proactivity"
						>
							{MobileLayout.enabled ? (
								<RadioGroup
									options={autonomyOptions}
									value={KonvoConnections.assistantAutonomy}
									disabled={saving}
									onChange={(autonomy) => {
										setSaving(true);
										void KonvoConnections.updateAssistantAutonomy(autonomy)
											.catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)))
											.finally(() => setSaving(false));
									}}
									aria-label={i18n._(msg`Assistant proactivity`)}
									data-flx="user.konvo-assistant-tab.proactivity"
								/>
							) : (
								<SettingsChoiceCards<KonvoAssistantAutonomy>
									options={autonomyOptions.map((option) => ({
										value: option.value,
										name: String(option.name),
										desc: option.desc ? String(option.desc) : undefined,
									}))}
									value={KonvoConnections.assistantAutonomy}
									disabled={saving}
									onChange={(autonomy) => {
										setSaving(true);
										void KonvoConnections.updateAssistantAutonomy(autonomy)
											.catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)))
											.finally(() => setSaving(false));
									}}
									aria-label={i18n._(msg`Assistant proactivity`)}
									className={assistantStyles.autonomy}
									data-flx="user.konvo-assistant-tab.proactivity"
								/>
							)}
						</SettingsSection>

						<SettingsSection
							id="konvo-assistant-replies"
							title={<Trans>Replies</Trans>}
							data-flx="user.konvo-assistant-tab.section.replies"
						>
							<Switch
								label={i18n._(msg`Show replies as they are written`)}
								description={i18n._(
									msg`The reply appears within a few seconds and fills in as the assistant writes, instead of arriving in one piece at the end. Still being tested.`,
								)}
								value={KonvoConnections.assistantSettings?.streaming === true}
								disabled={saving || KonvoConnections.assistantSettings == null}
								onChange={(streaming) => {
									setSaving(true);
									void KonvoConnections.updateAssistantSettings({streaming})
										.catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)))
										.finally(() => setSaving(false));
								}}
								icon={<ChatCircleDotsIcon size={20} />}
								data-flx="user.konvo-assistant-tab.streaming"
							/>
							{KonvoConnections.assistantSettings && (
								<AssistantTimezoneRow
									timezone={KonvoConnections.assistantSettings.effective_timezone}
									disabled={saving}
									onUseDevice={(timezone) => {
										setSaving(true);
										void KonvoConnections.updateAssistantSettings({timezone})
											.catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)))
											.finally(() => setSaving(false));
									}}
								/>
							)}
						</SettingsSection>

						<KonvoAssistantProactiveSection />

						<SettingsSection
							id="konvo-assistant-enabled"
							title={<Trans>Assistant</Trans>}
							data-flx="user.konvo-assistant-tab.section.enabled"
						>
							<Switch
								label={i18n._(ENABLED_DESCRIPTOR)}
								description={i18n._(ENABLED_DESCRIPTION_DESCRIPTOR)}
								value={status?.enabled === true}
								disabled={saving}
								onChange={(value) => void patch({enabled: value})}
								icon={<SparkleIcon size={20} />}
								data-flx="user.konvo-assistant-tab.enabled"
							/>
							<SettingsLinkRow
								icon={<ChatCircleIcon size={20} />}
								title={i18n._(OPEN_DESCRIPTOR)}
								onClick={() => void openKonvoAssistant()}
								data-flx="user.konvo-assistant-tab.open"
							/>
							<SettingsLinkRow
								icon={<ArrowCounterClockwiseIcon size={20} />}
								title={i18n._(RESET_DESCRIPTOR)}
								disabled={saving}
								onClick={() =>
									void (
										provider === 'codex'
											? KonvoConnections.resetCodexConversation()
											: KonvoConnections.resetClaudeConversation()
									)
										.then(() => ToastCommands.success(i18n._(RESET_DONE_DESCRIPTOR)))
										.catch(() => ToastCommands.error(i18n._(FAILED_DESCRIPTOR)))
								}
								data-flx="user.konvo-assistant-tab.reset"
							/>
						</SettingsSection>
					</>
				)}

				<SettingsSection
					id="konvo-assistant-memory"
					title={i18n._(KNOWS_M.settingsSectionTitle)}
					data-flx="user.konvo-assistant-tab.section.memory"
				>
					<SettingsLinkRow
						icon={<BrainIcon size={20} />}
						title={i18n._(KNOWS_TAB_DESCRIPTOR)}
						description={i18n._(KNOWS_M.settingsLinkDescription)}
						trailing={
							<span className={assistantStyles.knowsCount}>
								{KonvoKnows.noteCount != null && i18n._(KNOWS_M.settingsLink, {count: KonvoKnows.noteCount})}
								<CaretRightIcon size={18} weight="bold" />
							</span>
						}
						onClick={openKnowsTab}
						data-flx="user.konvo-assistant-tab.open-knows"
					/>
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default KonvoAssistantSettingsTab;
